import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import { selectNavTasksData } from "../../../store/robot-configurations/robot-configurations-selectors";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import OptionSwitchInput, { PlaceholderOptions } from "../../Inputs/OptionSwitchInput";
import CustomSplitColumnCard from "../../misc/CustomSplitColumnCard";

const WaypointsConfiguration: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { getNavTasks, patchNavTasks, patchNavTasksRestSet } = useSharedActions();

  const tasksData = useSharedSelector((state) => selectNavTasksData(state));

  const [snackbarGuard, setSnackbarGuard] = useState(false);

  useEffect(() => {
    if (tasksData.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getNavTasks());
    }
  }, [dispatch, getNavTasks, tasksData.rest.getStatus]);

  const useWaypointToZoneHandler = (value: PlaceholderOptions) => {
    setSnackbarGuard(true);
    dispatch(patchNavTasks({ ZoneCleaning: { approachAlongWaypoints: value === PlaceholderOptions.True } }));
  };

  const useWaypointWhenHomingHandler = (value: PlaceholderOptions) => {
    setSnackbarGuard(true);
    dispatch(patchNavTasks({ ToStart: { alongWaypoints: value === PlaceholderOptions.True } }));
  };

  const handleConfigurationSnackbarClose = () => {
    setSnackbarGuard(false);
    dispatch(patchNavTasksRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  };

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t("waypoints-configuration-title")}
        content={[
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <Typography>{t("label-when-to-use-waypoints")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <Stack spacing={2}>
                <OptionSwitchInput
                  label={t("waypoints-to-zone-label")}
                  helpText={t("waypoints-to-zone-info")}
                  options={[
                    {
                      label: t("cleaning-configuration-switch-on"),
                      value: PlaceholderOptions.True,
                    },
                    {
                      label: t("cleaning-configuration-switch-off"),
                      value: PlaceholderOptions.False,
                    },
                  ]}
                  loading={tasksData.data == null}
                  value={
                    tasksData.data?.ZoneCleaning.approachAlongWaypoints
                      ? PlaceholderOptions.True
                      : PlaceholderOptions.False
                  }
                  onChange={useWaypointToZoneHandler}
                />
                <OptionSwitchInput
                  label={t("waypoints-to-start-label")}
                  helpText={t("waypoints-to-start-info")}
                  options={[
                    {
                      label: t("cleaning-configuration-switch-on"),
                      value: PlaceholderOptions.True,
                    },
                    {
                      label: t("cleaning-configuration-switch-off"),
                      value: PlaceholderOptions.False,
                    },
                  ]}
                  loading={tasksData.data == null}
                  value={tasksData.data?.ToStart.alongWaypoints ? PlaceholderOptions.True : PlaceholderOptions.False}
                  onChange={useWaypointWhenHomingHandler}
                />
              </Stack>
            ),
          },
        ]}
      />

      <CustomSnackbar
        title={t("error-saving-waypoints-configurations")}
        open={snackbarGuard && tasksData.rest.patchStatus === HttpFetchStatus.Failed}
        onClose={handleConfigurationSnackbarClose}
        textMessages={getSaveErrorMessages(tasksData.rest.patchError)}
        severity="error"
      />
      <CustomSnackbar
        title={t("success-saving-waypoints-configurations")}
        severity="success"
        open={snackbarGuard && tasksData.rest.patchStatus === HttpFetchStatus.Success}
        onClose={handleConfigurationSnackbarClose}
      />
    </React.Fragment>
  );
};

export default WaypointsConfiguration;
